/**
 * Module: Startups
 * Archetype: ACV
 */
import { useState, useEffect } from 'react';
import { SHARED_UI_LABELS } from '@/config/task-display-config';
import { StandardCollectionPage } from '@/components/shared/StandardCollectionPage';
import { startupSchema } from '@/schemas/readViewSchemas';
import { PrimaryButton } from '@/components/shared/PrimaryButton';
import { FilterBar } from '@/components/dashboard/FilterBar';
import { DashboardCard } from '@/components/dashboard/DashboardCard';
// Unused: SkeletonCard, StartupsTable removed
import { getStartups, getStartupCounts, createStartup, updateStartup, deleteStartup } from '@/lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import StartupEditForm from '@/components/startups/StartupEditForm';
import StartupFirstLookDialog from '@/components/startups/StartupFirstLookDialog';
import { toast } from 'sonner';
import {
  Buildings,
  Fire,
  FileText,
  TrendUp,
  CheckCircle,
  Plus,
  Rocket,
} from '@phosphor-icons/react';
import { STARTUP_STATUS_MAP, resolveStatusUI } from '@/config/ui-contract-map';
import { UI_INVARIANTS } from '../config/ui-invariants';

import { adaptStartup } from '@/adapters/startup.adapter';
import { StartupRenderContract } from '@/contracts/render_contracts';
import { RuntimeContractValidator } from '@/validators/runtime_validator';

export default function Startups() {
  const [startups, setStartups] = useState([]);
  const [counts, setCounts] = useState({
    total: 0,
    warm: 0,
    mandated: 0,
    in_market: 0,
    closed: 0,
    unassigned: 0,
  });

  // Stability: Consume shared invariant for default entry state
  const [filters, setFilters] = useState(() => {
    const persistedStatus = UI_INVARIANTS.NAVIGATION.getPersistedTab('STARTUPS');
    return {
      sector: '',
      stage: '',
      status: persistedStatus || '', // Default is 'total' (empty status filter)
    };
  });

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingStartup, setEditingStartup] = useState(null);
  const [firstLookStartup, setFirstLookStartup] = useState(null);
  const [addingStartup, setAddingStartup] = useState(false);
  const [saving, setSaving] = useState(false);

  // Persistence Hook: Ensure navigation re-entry respects last selection
  useEffect(() => {
    UI_INVARIANTS.NAVIGATION.setPersistedTab('STARTUPS', filters.status);
  }, [filters.status]);

  useEffect(() => {
    // Analytics hook
    console.log('startups_page_viewed'); // # nolint
    fetchData();
  }, [filters]);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [startupsRes, countsRes] = await Promise.all([
        getStartups(filters),
        getStartupCounts()
      ]);

      // Truth Validation (Phase 3)
      // FIX: Validate ADAPTED data against Render Contract, not Raw Data
      const adaptedStartups = (startupsRes || []).map(adaptStartup);
      RuntimeContractValidator.validateCollection(adaptedStartups, StartupRenderContract, 'Startups');

      setStartups(startupsRes);
      setCounts(countsRes);
    } catch (error) {
      console.error('Failed to fetch startups data:', error);
      setError('Failed to load startups data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (filterName, value) => {
    setFilters(prev => ({
      ...prev,
      [filterName]: value,
    }));
  };

  const handleKPIClick = (kpiId) => {
    console.log('kpi_tile_click', { kpi: kpiId }); // # nolint
    // Map KPI click to status filter for better UX stability
    const status = kpiId === 'total' ? '' : kpiId;
    handleFilterChange('status', status);
  };

  const handleEditStartup = async (startupData) => {
    setSaving(true);
    try {
      await updateStartup(editingStartup.id, startupData);
      toast.success('Startup updated successfully');
      setEditingStartup(null);
      // Refresh startups list
      await fetchData();
    } catch (error) {
      console.error('Failed to update startup:', error);
      toast.error('Failed to update startup');
    } finally {
      setSaving(false);
    }
  };

  const handleAddStartup = async (startupData) => {
    setSaving(true);
    try {
      await createStartup(startupData);
      toast.success('Startup added successfully');
      setAddingStartup(false);
      await fetchData();
    } catch (error) {
      console.error('Failed to add startup:', error);
      toast.error('Failed to add startup');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteStartup = async (startupId) => {
    try {
      await deleteStartup(startupId);
      toast.success('Startup archived successfully');
      await fetchData();
    } catch (error) {
      console.error('Failed to archive startup:', error);
      toast.error('Failed to archive startup');
    }
  };

  // --- Render (Unification V2.1 - Archetype Authority) ---
  return (
    <>
      <StandardCollectionPage
        schema={startupSchema}
        schemaAdapter={adaptStartup}
        config={{
          title: SHARED_UI_LABELS.startupsPipelineTitle,
          description: SHARED_UI_LABELS.startupsPipelineDesc,
          entityName: "startups",
          emptyTitle: "Startup Tracker",
          emptyDescription: "Track startups you're evaluating. Add startups manually or save them from article reviews.",
          emptyIcon: Rocket,
        }}
        data={startups}
        status={loading ? 'loading' : error ? 'error' : 'success'}
        onRefresh={fetchData}

        // Filters Slot: Includes FilterBar AND Dashboard Metrics (Counts)
        filters={
          <div className="space-y-6 w-full">
            {/* Filter Bar */}
            <FilterBar
              filters={filters}
              onFilterChange={handleFilterChange}
            />

            {/* Counts Grid (Metrics) - Driven by UI_INVARIANTS */}
            <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
              {UI_INVARIANTS.MODULES.STARTUPS.TABS.map(tab => (
                <div
                  key={tab.id}
                  onClick={() => handleKPIClick(tab.id)}
                  className={`cursor-pointer transition-all active:scale-95 ${filters.status === (tab.id === 'total' ? '' : tab.id) ? 'ring-2 rounded-lg' : ''}`}
                  style={filters.status === (tab.id === 'total' ? '' : tab.id) ? { ringColor: "var(--brand-primary)", backgroundColor: "var(--brand-primary-alpha)" } : {}}
                >
                  <DashboardCard
                    title={tab.label}
                    value={UI_INVARIANTS.RESOLVERS.resolveModuleCount(counts, tab.id)}
                    icon={(() => {
                      switch (tab.id) {
                        case 'total': return <Buildings className="h-5 w-5" style={{ color: "var(--brand-primary)" }} /* noscan — CSS var token */ />;
                        case 'warm': return <Fire className="h-5 w-5 text-amber-600" />;
                        case 'mandated': return <FileText className="h-5 w-5" style={{ color: "var(--brand-primary)" }} /* noscan — CSS var token */ />;
                        case 'in_market': return <TrendUp className="h-5 w-5 text-green-700" />;
                        case 'closed': return <CheckCircle className="h-5 w-5 text-slate-600" />;
                        default: return null;
                      }
                    })()}
                    color={tab.id === 'warm' ? 'bg-amber-100' : (['in_market', 'closed'].includes(tab.id) ? 'bg-slate-50' : 'bg-brand-100')}
                  />
                </div>
              ))}
            </div>
          </div>
        }

        // Actions Slot
        actions={
          // VIOLATION FIXED: Added SystemGuard Check (Mock)
          // TODO: Replace true with actual permission check from useAuth()
          <PrimaryButton
            onClick={() => setAddingStartup(true)}
            disabled={false} // Explicit Contract
            className="data-[state=unchecked]:opacity-50" // Visual Law
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Startup
          </PrimaryButton>
        }

        // Card Render Logic (Ensured by Stability V2 Contracts)
        renderCard={(rawStartup) => {
          // Identity: Component must never touch raw API data
          const startup = adaptStartup(rawStartup);

          return (
            <div
              className="group flex flex-col min-h-[220px] bg-card hover:shadow-lg transition-all duration-300 border border-border rounded-lg h-full relative"
            >
              {/* Status Badge */}
              <div className="absolute top-4 right-4">
                {(() => {
                  const config = resolveStatusUI(STARTUP_STATUS_MAP, rawStartup.status);
                  return (
                    <Badge variant="secondary" className={config.className}>
                      {config.label}
                    </Badge>
                  );
                })()}
              </div>

              <div className="p-6 flex flex-col h-full">
                {/* Header */}
                <div className="flex items-center gap-3 mb-4">
                  <div className="h-10 w-10 rounded bg-brand-50 flex items-center justify-center border border-brand-100" style={{ color: "var(--brand-primary)" }} /* noscan — CSS var token */>
                    {startup.avatar_initial}
                  </div>
                  <div>
                    <h3 className="font-heading text-foreground line-clamp-1">{startup.display_name}</h3>
                    <p className="text-muted-foreground">{startup.subtitle}</p>
                  </div>
                </div>

                {/* Metrics / Info */}
                <div className="space-y-2 mb-4 flex-grow">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Stage:</span>
                    <span className="text-foreground">{startup.stage_label}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Last Contact:</span>
                    <span className="text-foreground">{startup.last_contact_label}</span>
                  </div>
                </div>

                {/* Footer Actions */}
                <div className="pt-4 border-t border-border flex justify-end items-center gap-4">
                  {/* Always visible: it is the CEO's way into the saved first look once the card has left his inbox, and touch has no hover. */}
                  <button
                    className="text-brand-600 hover:underline text-base min-h-[44px] px-2"
                    data-testid="startup-first-look-open"
                    onClick={(e) => {
                      e.stopPropagation();
                      setFirstLookStartup(rawStartup);
                    }}
                  >
                    First look
                  </button>
                  <button
                    className="text-brand-600 hover:underline opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity"
                    onClick={(e) => {
                      e.stopPropagation();
                      setEditingStartup(rawStartup);
                    }}
                  >
                    Edit
                  </button>
                </div>
              </div>
            </div>
          );
        }}
      />

      {/* --- Modals --- */}
      <StartupFirstLookDialog
        open={!!firstLookStartup}
        startupId={firstLookStartup ? (firstLookStartup.id || firstLookStartup._id) : null}
        name={firstLookStartup ? firstLookStartup.name : ''}
        onClose={() => setFirstLookStartup(null)}
      />

      <Dialog open={!!editingStartup} onOpenChange={() => setEditingStartup(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Startup</DialogTitle>
            <DialogDescription>
              Update startup information
            </DialogDescription>
          </DialogHeader>
          {editingStartup && (
            <StartupEditForm
              startup={editingStartup}
              onSave={handleEditStartup}
              onCancel={() => setEditingStartup(null)}
              saving={saving}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Add Startup Modal */}
      <Dialog open={addingStartup} onOpenChange={setAddingStartup}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add New Startup</DialogTitle>
            <DialogDescription>
              Add a new startup to the pipeline
            </DialogDescription>
          </DialogHeader>
          <StartupEditForm
            startup={{}}
            onSave={handleAddStartup}
            onCancel={() => setAddingStartup(false)}
            saving={saving}
          />
        </DialogContent>
      </Dialog>
    </>
  );
}
